import type {
  ExcelSyncPackagePayload,
  ExcelSyncPackageResponse,
  SubmissionSync,
  SubmissionSyncCreatePayload,
  SubmissionSyncCreateResponse,
  SubmissionSyncDelta,
  SubmissionSyncSidecar,
} from "../types/submissionSync";
import { downloadBlob } from "../lib/http/downloadBlob";
import {
  SUBMISSION_SYNC_UNAVAILABLE,
  normalizeExcelSyncPackageResponse,
  normalizeSubmissionSyncCreateResponse,
  normalizeSubmissionSyncDelta,
  normalizeSubmissionSyncList,
  normalizeSubmissionSyncSidecar,
} from "../lib/submissionSync/normalize";
import { buildExcelSyncSubmissionsQuery } from "../lib/submissionSync/query";
import { ApiError, apiDownload, apiRequest } from "./client";

function excelSyncPath(formId: number): string {
  return `/api/v1/forms/${formId}/excel-sync`;
}

function tokenManagePath(formId: number): string {
  return `/api/v1/forms/${formId}/submission-sync`;
}

function tokenPublicPath(token: string): string {
  return `/api/v1/submission-syncs/${encodeURIComponent(token)}`;
}

function mapTokenError(err: unknown): never {
  if (err instanceof ApiError && err.status === 404) {
    throw new ApiError(SUBMISSION_SYNC_UNAVAILABLE, 404, err.errors);
  }
  throw err;
}

function publicOptions() {
  return {
    auth: false as const,
    skipUnauthorizedHandler: true,
  };
}

/** POST /forms/{id}/excel-sync/package — sidecar без секрета, auth=chm. */
export function createExcelSyncPackage(
  formId: number,
  payload: ExcelSyncPackagePayload = {},
): Promise<ExcelSyncPackageResponse> {
  return apiRequest<unknown>(`${excelSyncPath(formId)}/package`, {
    method: "POST",
    body: JSON.stringify(payload),
  }).then(normalizeExcelSyncPackageResponse);
}

/** GET /forms/{id}/excel-sync/submissions — дельта, form.auth. */
export function fetchExcelSyncDelta(
  formId: number,
  params: {
    afterId?: number;
    count?: number;
    fieldIds?: number[];
    metaKeys?: ExcelSyncPackagePayload["meta"];
    order?: number[];
  } = {},
): Promise<SubmissionSyncDelta> {
  const query = buildExcelSyncSubmissionsQuery(params);
  return apiRequest<unknown>(
    `${excelSyncPath(formId)}/submissions${query}`,
  ).then(normalizeSubmissionSyncDelta);
}

/** Опционально: анонимный token-sync. */

export function fetchSubmissionSyncs(
  formId: number,
): Promise<SubmissionSync[]> {
  return apiRequest<unknown>(tokenManagePath(formId)).then(
    normalizeSubmissionSyncList,
  );
}

export function createSubmissionSync(
  formId: number,
  payload: SubmissionSyncCreatePayload = {},
): Promise<SubmissionSyncCreateResponse> {
  return apiRequest<unknown>(tokenManagePath(formId), {
    method: "POST",
    body: JSON.stringify(payload),
  }).then(normalizeSubmissionSyncCreateResponse);
}

export function revokeSubmissionSync(
  formId: number,
  syncId: number,
): Promise<void> {
  return apiRequest<void>(`${tokenManagePath(formId)}/${syncId}`, {
    method: "DELETE",
  });
}

export function fetchSubmissionSyncPublic(
  token: string,
): Promise<SubmissionSyncSidecar> {
  return apiRequest<unknown>(tokenPublicPath(token), publicOptions())
    .then((body) => {
      const sidecar = normalizeSubmissionSyncSidecar(body);
      if (!sidecar) throw new Error("Некорректный ответ Excel-синка");
      return sidecar;
    })
    .catch(mapTokenError);
}

export function fetchSubmissionSyncSidecar(
  token: string,
): Promise<SubmissionSyncSidecar> {
  return apiRequest<unknown>(
    `${tokenPublicPath(token)}/sidecar`,
    publicOptions(),
  )
    .then((body) => {
      const sidecar = normalizeSubmissionSyncSidecar(body);
      if (!sidecar) throw new Error("Некорректный sidecar Excel-синка");
      return sidecar;
    })
    .catch(mapTokenError);
}

export async function exportSubmissionSyncXlsx(token: string): Promise<string> {
  try {
    const { blob, filename } = await apiDownload(
      `${tokenPublicPath(token)}/export?format=xlsx`,
      publicOptions(),
    );
    downloadBlob(blob, filename);
    return filename;
  } catch (err) {
    mapTokenError(err);
  }
}

export function fetchSubmissionSyncDelta(
  token: string,
  params: { afterId?: number; count?: number } = {},
): Promise<SubmissionSyncDelta> {
  const query = new URLSearchParams({
    afterId: String(params.afterId ?? 0),
    count: String(params.count ?? 100),
  });
  return apiRequest<unknown>(
    `${tokenPublicPath(token)}/submissions?${query}`,
    publicOptions(),
  )
    .then(normalizeSubmissionSyncDelta)
    .catch(mapTokenError);
}
